<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import {
  patientServiceAssignmentsApi,
  type PatientServiceAssignment,
  type PatientServiceAssignmentCreateForm,
  type PatientServiceAssignmentUpdateForm,
} from '@/api/patient-service-assignments';
import { patientServiceCategoriesApi } from '@/api/patient-service-categories';
import { usePermissions } from '@/composables/usePermissions';
import { todayIso } from '@/utils/calendar-events';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

const { t } = useI18n();
const { hasPermission } = usePermissions();
const queryClient = useQueryClient();

interface Props {
  patientId: number;
}

const props = defineProps<Props>();

// Fired after any mutation with the patient's now-current assignment (or null), so the
// profile banner chip stays in sync without a page reload.
const emit = defineEmits<{
  (e: 'currentChanged', assignment: PatientServiceAssignment | null): void;
}>();

const canEdit = hasPermission.value('patients', 'update');

const { data: assignments, isPending } = useQuery({
  queryKey: ['patientServiceAssignments', props.patientId],
  queryFn: () => patientServiceAssignmentsApi.index(props.patientId),
  initialData: [],
});

const { data: categories } = useQuery({
  queryKey: ['patientServiceCategories', 'forFilters'],
  queryFn: patientServiceCategoriesApi.indexForFilters,
  initialData: [],
  enabled: canEdit,
});

const editedAssignment = ref<PatientServiceAssignment | null>(null);
const deletedAssignment = ref<PatientServiceAssignment | null>(null);
const editForm = ref<PatientServiceAssignmentUpdateForm>({});
const showAssignDialog = ref(false);
const assignForm = ref<PatientServiceAssignmentCreateForm>({ categoryId: 0 });
const mutationError = ref<string | null>(null);

function openAssign() {
  mutationError.value = null;
  const current = assignments.value.find((assignment) => assignment.current);
  assignForm.value = {
    categoryId: current?.categoryId ?? 0,
    subcategoryId: current?.subcategoryId ?? null,
    startsOn: todayIso(),
  };
  showAssignDialog.value = true;
}

function openEdit(assignment: PatientServiceAssignment) {
  mutationError.value = null;
  editForm.value = {
    startsOn: assignment.startsOn,
    endsOn: assignment.endsOn,
    patientServiceCategoryId: assignment.categoryId,
    patientServiceSubcategoryId: assignment.subcategoryId,
  };
  editedAssignment.value = assignment;
}

function withArchivedOption(
  items: Array<{ value: number; title: string }>,
  id: number | null | undefined,
  name: string | null | undefined,
) {
  if (!id || items.some((item) => item.value === id)) return items;

  return [{ value: id, title: `${name} (${t('serviceAssignments.archived')})` }, ...items];
}

const editCategoryItems = computed(() => withArchivedOption(
  categories.value.map((category) => ({ value: category.id, title: category.name })),
  editedAssignment.value?.categoryId,
  editedAssignment.value?.categoryName,
));

function subcategoryItemsFor(categoryId: number | null | undefined) {
  const category = categories.value.find((candidate) => candidate.id === categoryId);

  return (category?.subcategories ?? []).map((subcategory) => ({
    value: subcategory.id, title: subcategory.name,
  }));
}

const editSubcategoryItems = computed(() => {
  const items = subcategoryItemsFor(editForm.value.patientServiceCategoryId);
  const edited = editedAssignment.value;
  if (!edited || edited.categoryId !== editForm.value.patientServiceCategoryId) {
    return items;
  }

  return withArchivedOption(items, edited.subcategoryId, edited.subcategoryName);
});

function onEditCategoryChange() {
  editForm.value.patientServiceSubcategoryId = null;
}

// New assignments only offer kept catalog entries — no archived fallback here.
const assignCategoryItems = computed(() =>
  categories.value.map((category) => ({ value: category.id, title: category.name })));
const assignSubcategoryItems = computed(() => subcategoryItemsFor(assignForm.value.categoryId));

function onAssignCategoryChange() {
  assignForm.value.subcategoryId = null;
}

function extractError(error: AxiosError): string {
  const data = error.response?.data as
    { errors?: Record<string, string[]> | string; message?: string } | undefined;
  if (typeof data?.errors === 'string') return data.errors;
  if (data?.errors) return Object.values(data.errors).flat().join('. ');

  return t('userSession.defaultError');
}

// Refresh with a direct fetch (not just invalidate) so the emitted current assignment
// reflects the post-mutation state deterministically.
async function refreshAndEmitCurrent() {
  const list = await patientServiceAssignmentsApi.index(props.patientId);
  queryClient.setQueryData(['patientServiceAssignments', props.patientId], list);
  emit('currentChanged', list.find((assignment) => assignment.current) ?? null);
}

const assignMutation = useMutation({
  mutationFn: () => patientServiceAssignmentsApi.create(props.patientId, assignForm.value),
  onSuccess: async () => {
    await refreshAndEmitCurrent();
    showAssignDialog.value = false;
  },
  onError: (error: AxiosError) => { mutationError.value = extractError(error); },
});

const updateMutation = useMutation({
  mutationFn: () => patientServiceAssignmentsApi.update(
    props.patientId,
    editedAssignment.value!.id,
    { ...editForm.value, endsOn: editForm.value.endsOn || null },
  ),
  onSuccess: async () => {
    await refreshAndEmitCurrent();
    editedAssignment.value = null;
  },
  onError: (error: AxiosError) => { mutationError.value = extractError(error); },
});

const deleteMutation = useMutation({
  mutationFn: () => patientServiceAssignmentsApi.destroy(
    props.patientId, deletedAssignment.value!.id,
  ),
  onSuccess: async () => {
    await refreshAndEmitCurrent();
    deletedAssignment.value = null;
  },
  onError: (error: AxiosError) => { mutationError.value = extractError(error); },
});

function assignmentTitle(assignment: PatientServiceAssignment): string {
  const archivedSuffix = assignment.categoryDiscarded ?
    ` (${t('serviceAssignments.archived')})` : '';
  const base = `${assignment.categoryName}${archivedSuffix}`;

  return assignment.subcategoryName ? `${base} • ${assignment.subcategoryName}` : base;
}

function assignmentPeriod(assignment: PatientServiceAssignment): string {
  if (!assignment.humanEndsOn) {
    return t('serviceAssignments.sinceDate', { date: assignment.humanStartsOn });
  }

  return t('serviceAssignments.dateRange', {
    from: assignment.humanStartsOn, to: assignment.humanEndsOn,
  });
}
</script>
<template>
  <v-card>
    <v-card-title class="d-flex align-center text-subtitle-1 font-weight-bold">
      {{ t('serviceAssignments.timelineTitle') }}
      <v-spacer />
      <v-btn
        v-if="canEdit"
        color="primary"
        size="small"
        variant="tonal"
        prepend-icon="mdi-tag-plus"
        @click="openAssign"
      >
        {{ t('serviceAssignments.assignCta') }}
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-progress-circular
        v-if="isPending"
        indeterminate
        color="primary"
      />
      <p
        v-else-if="assignments.length === 0"
        class="text-body-2 text-medium-emphasis"
      >
        {{ t('serviceAssignments.empty') }}
      </p>
      <v-timeline
        v-else
        side="end"
        density="compact"
        align="start"
      >
        <v-timeline-item
          v-for="assignment in assignments"
          :key="assignment.id"
          :dot-color="assignment.current ? 'primary' : 'grey'"
          size="small"
        >
          <div class="d-flex align-center flex-wrap ga-2">
            <span class="font-weight-medium">{{ assignmentTitle(assignment) }}</span>
            <v-chip
              v-if="assignment.current"
              color="primary"
              size="x-small"
            >
              {{ t('serviceAssignments.current') }}
            </v-chip>
            <template v-if="canEdit">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                variant="text"
                :aria-label="t('serviceAssignments.edit')"
                @click="openEdit(assignment)"
              />
              <v-btn
                icon="mdi-delete"
                size="x-small"
                variant="text"
                color="error"
                :aria-label="t('serviceAssignments.delete')"
                @click="mutationError = null; deletedAssignment = assignment"
              />
            </template>
          </div>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ assignmentPeriod(assignment) }}
          </p>
          <p
            v-if="assignment.assignedByName"
            class="text-caption text-medium-emphasis mb-0"
          >
            {{ t('serviceAssignments.assignedBy', { name: assignment.assignedByName }) }}
          </p>
        </v-timeline-item>
      </v-timeline>
    </v-card-text>

    <responsive-dialog
      v-model="showAssignDialog"
      max-width="500"
      :mobile-fullscreen="false"
    >
      <v-card class="pa-4">
        <v-card-title>{{ t('serviceAssignments.assignCta') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="assignForm.categoryId"
            :items="assignCategoryItems"
            :label="t('serviceAssignments.category')"
            variant="outlined"
            class="mb-4"
            hide-details
            @update:model-value="onAssignCategoryChange"
          />
          <v-select
            v-if="assignSubcategoryItems.length > 0"
            v-model="assignForm.subcategoryId"
            :items="assignSubcategoryItems"
            :label="t('serviceAssignments.subcategory')"
            variant="outlined"
            clearable
            class="mb-4"
            hide-details
          />
          <v-text-field
            v-model="assignForm.startsOn"
            type="date"
            :label="t('serviceAssignments.effectiveFrom')"
            :hint="t('serviceAssignments.effectiveFromHint')"
            persistent-hint
            variant="outlined"
          />
          <v-alert
            v-if="mutationError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ mutationError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showAssignDialog = false">
            {{ t('serviceAssignments.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!assignForm.categoryId"
            :loading="assignMutation.isPending.value"
            @click="assignMutation.mutate()"
          >
            {{ t('serviceAssignments.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </responsive-dialog>

    <responsive-dialog
      :model-value="!!editedAssignment"
      max-width="500"
      :mobile-fullscreen="false"
      @update:model-value="editedAssignment = null"
    >
      <v-card
        v-if="editedAssignment"
        class="pa-4"
      >
        <v-card-title>{{ t('serviceAssignments.edit') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="editForm.patientServiceCategoryId"
            :items="editCategoryItems"
            :label="t('serviceAssignments.category')"
            variant="outlined"
            class="mb-4"
            hide-details
            @update:model-value="onEditCategoryChange"
          />
          <v-select
            v-if="editSubcategoryItems.length > 0"
            v-model="editForm.patientServiceSubcategoryId"
            :items="editSubcategoryItems"
            :label="t('serviceAssignments.subcategory')"
            variant="outlined"
            clearable
            class="mb-4"
            hide-details
          />
          <v-text-field
            v-model="editForm.startsOn"
            type="date"
            :label="t('serviceAssignments.startsOn')"
            variant="outlined"
            class="mb-4"
            hide-details
          />
          <v-text-field
            v-model="editForm.endsOn"
            type="date"
            :label="t('serviceAssignments.endsOn')"
            variant="outlined"
            hide-details
          />
          <v-alert
            v-if="mutationError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ mutationError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="editedAssignment = null">
            {{ t('serviceAssignments.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="updateMutation.isPending.value"
            @click="updateMutation.mutate()"
          >
            {{ t('serviceAssignments.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </responsive-dialog>

    <responsive-dialog
      :model-value="!!deletedAssignment"
      max-width="500"
      :mobile-fullscreen="false"
      @update:model-value="deletedAssignment = null"
    >
      <v-card
        v-if="deletedAssignment"
        class="pa-4"
      >
        <v-card-title>{{ t('serviceAssignments.delete') }}</v-card-title>
        <v-card-text>
          <p>{{ assignmentTitle(deletedAssignment) }} — {{ assignmentPeriod(deletedAssignment) }}</p>
          <p class="text-body-2 text-medium-emphasis">
            {{ t('serviceAssignments.deleteConfirmation') }}
          </p>
          <v-alert
            v-if="mutationError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ mutationError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deletedAssignment = null">
            {{ t('serviceAssignments.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteMutation.isPending.value"
            @click="deleteMutation.mutate()"
          >
            {{ t('serviceAssignments.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </responsive-dialog>
  </v-card>
</template>
